import { motion } from "motion/react";
import { Check, CircleDashed, CircleDot, Loader2, Minus, X } from "lucide-react";
import { bi, type Bi, type Tone } from "../mission/engine";
import { count } from "../mission/text";
import type { DocWidget, Widget } from "../mission/widgets";
import { TONE_COLOR, Typewriter, useT } from "./kit";
import { MemoryWidget } from "./Memory";

type Size = "card" | "focus";

/** Draws an agent's intermediate result. `card` fits the swarm card; `focus` is the large view. */
export function WidgetView({ widget, size = "card" }: { widget?: Widget; size?: Size }) {
  if (!widget) return <Idle />;
  switch (widget.kind) {
    case "doc": return <DocView w={widget} size={size} />;
    case "facts": return <Facts rows={widget.rows} size={size} />;
    case "checklist": return <Checklist w={widget} size={size} />;
    case "meters": return <Meters w={widget} size={size} />;
    case "bars": return <Bars w={widget} size={size} />;
    case "gauge": return <GaugeView w={widget} size={size} />;
    case "stake": return <Stake w={widget} size={size} />;
    case "timeline": return <Timeline w={widget} size={size} />;
    case "cards": return <Cards w={widget} size={size} />;
    case "graph": return <Graph w={widget} size={size} />;
    case "series": return <Series w={widget} size={size} />;
    case "text": return <TextDoc w={widget} size={size} />;
    case "entities": return <Entities w={widget} size={size} />;
    case "memory": return <MemoryWidget w={widget} size={size} />;
  }
}

function Idle() {
  return (
    <div className="flex h-full items-center justify-center gap-1.5 opacity-60">
      {[0, 1, 2].map((i) => <motion.span key={i} className="size-1.5 rounded-full" style={{ background: "var(--sq)" }} animate={{ opacity: [0.2, 1, 0.2], y: [0, -3, 0] }} transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.15 }} />)}
    </div>
  );
}

// ---------------------------------------------------------------- document

/** A page being read: bars for lines (card) or the real text (focus), the reader's marks and the scanning line. */
export function DocView({ w, size }: { w: DocWidget; size: Size }) {
  const t = useT();
  if (size === "focus") {
    return (
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_260px]">
        <div className="agx-page p-5" style={{ minHeight: 360 }}>
          <div className="agx-scanline" style={{ top: `${Math.max(2, w.scan * 100)}%`, opacity: w.state === "done" ? 0 : 1 }} />
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider agx-faint">{t(w.docType)} · {t(count(w.pages, "pages"))}</p>
          {/* The document's own lines, as filed: read in the document, never translated in part. */}
          <div className="space-y-1.5 text-[13px] leading-6" dir="ltr" data-no-translate="">
            {w.lines.map((line, i) => <p key={i} className="text-[color:var(--agx-text)]/90">{marked(line.text, line.marks)}</p>)}
            {!w.lines.length ? <p className="agx-faint">{w.state === "extracting" ? t(bi("Extracting the text…", "يُستخرج النص…")) : "…"}</p> : null}
          </div>
        </div>
        <Facts rows={w.fields} size="focus" />
      </div>
    );
  }
  return (
    <div className="agx-page h-full px-2.5 py-2">
      <div className="agx-scanline" style={{ top: `${Math.max(3, w.scan * 100)}%`, opacity: w.state === "done" || w.state === "queued" ? 0 : 1 }} />
      <div className="space-y-[5px]">
        {(w.lines.length ? w.lines : PLACEHOLDER).slice(0, 12).map((line, i) => {
          const len = Math.max(0.25, Math.min(1, line.text.length / 78));
          return (
            <div key={i} className="relative h-[3px] rounded-full" style={{ width: `${len * 100}%`, background: w.lines.length ? "var(--agx-doc-bar)" : "var(--agx-doc-bar-dim)" }}>
              {line.marks?.map((m, j) => (
                <span key={j} className="absolute inset-y-[-1px] rounded-full" style={{ insetInlineStart: `${(m.from / Math.max(1, line.text.length)) * 100}%`, width: `${Math.max(6, ((m.to - m.from) / Math.max(1, line.text.length)) * 100)}%`, background: TONE_COLOR[m.tone] }} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
const PLACEHOLDER = Array.from({ length: 10 }, (_, i) => ({ text: "x".repeat(40 + ((i * 17) % 38)), marks: [] }));

function marked(text: string, marks?: Array<{ from: number; to: number; tone: Tone }>) {
  if (!marks?.length) return text;
  const out: React.ReactNode[] = [];
  let at = 0;
  marks.forEach((m, i) => {
    if (m.from > at) out.push(text.slice(at, m.from));
    out.push(<mark key={i} className={`agx-mark-${m.tone} rounded-[3px] px-0.5 text-[color:var(--agx-text)]`}>{text.slice(m.from, m.to)}</mark>);
    at = m.to;
  });
  if (at < text.length) out.push(text.slice(at));
  return out;
}

// ---------------------------------------------------------------- rows and lists

function Facts({ rows, size }: { rows: Array<{ label: Bi; value: Bi; tone?: Tone; note?: Bi }>; size: Size }) {
  const t = useT();
  return (
    <div className={size === "card" ? "space-y-1" : "space-y-2"}>
      {rows.slice(0, size === "card" ? 5 : 20).map((r, i) => (
        <motion.div key={`${r.label.en}-${i}`} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className={`flex items-start gap-2 ${size === "card" ? "text-[11.5px]" : "rounded-[5px] border border-[color:var(--agx-line)] agx-wash p-2.5 text-[12.5px]"}`}>
          <span className="mt-1.5 size-1.5 shrink-0 rounded-full" style={{ background: TONE_COLOR[r.tone ?? "info"] }} />
          <span className="min-w-0 flex-1">
            <span className="agx-dim">{t(r.label)}</span>{" "}
            <span className="font-medium text-[color:var(--agx-text)]">{t(r.value)}</span>
            {r.note ? <span className="agx-faint"> · {t(r.note)}</span> : null}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

const CHECK_ICON = { pending: CircleDashed, checking: Loader2, ok: Check, partial: CircleDot, missing: X, optional: Minus };
const CHECK_TONE = { pending: "var(--agx-faint)", checking: "var(--agx-cyan)", ok: "var(--agx-green)", partial: "var(--agx-amber)", missing: "var(--agx-rose)", optional: "var(--agx-faint)" };

function Checklist({ w, size }: { w: Extract<Widget, { kind: "checklist" }>; size: Size }) {
  const t = useT();
  const items = size === "card" ? w.items.slice(0, 12) : w.items;
  return (
    <div className="flex h-full flex-col">
      {w.caption ? <p className="mb-1 text-end text-[11px] font-semibold uppercase tracking-wider agx-faint">{t(w.caption)}</p> : null}
      <div className={size === "card" ? "grid grid-cols-2 gap-x-3 gap-y-[3px]" : "space-y-1.5"}>
        {items.map((item) => {
          const Icon = CHECK_ICON[item.state];
          return (
            <div key={item.key} className={`flex min-w-0 items-start gap-1.5 ${size === "card" ? "text-[11px]" : "text-[12.5px]"}`}>
              <Icon className={`mt-[2px] size-3 shrink-0 ${item.state === "checking" ? "agx-spin" : ""}`} style={{ color: CHECK_TONE[item.state] }} />
              <span className={`min-w-0 ${size === "card" ? "truncate" : ""} ${item.state === "pending" ? "agx-faint" : "text-[color:var(--agx-text)]/90"}`}>
                {t(item.label)}
                {size === "focus" && item.note ? <span className="block text-[11.5px] agx-dim">{t(item.note)}</span> : null}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Cards({ w, size }: { w: Extract<Widget, { kind: "cards" }>; size: Size }) {
  const t = useT();
  if (!w.cards.length) return <p className="flex h-full items-center text-[11.5px] leading-5 agx-dim">{w.empty ? t(w.empty) : "—"}</p>;
  return (
    <div className="space-y-1.5">
      {w.cards.slice(0, size === "card" ? 3 : 40).map((c, i) => (
        <motion.div key={`${c.title.en}-${i}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-md border border-[color:var(--agx-line)] agx-wash px-2 py-1.5" style={{ borderInlineStartColor: TONE_COLOR[c.tone ?? "info"], borderInlineStartWidth: 2 }}>
          <p className={`${size === "card" ? "truncate text-[11px]" : "text-[13px]"} font-medium`}>{t(c.title)}</p>
          {c.subtitle ? <p className={`${size === "card" ? "truncate text-[11px]" : "text-[12px]"} agx-dim`}>{t(c.subtitle)}</p> : null}
          {c.meta && size === "focus" ? <p className="mt-0.5 text-[11px] agx-faint">{t(c.meta)}</p> : null}
        </motion.div>
      ))}
    </div>
  );
}

function Entities({ w, size }: { w: Extract<Widget, { kind: "entities" }>; size: Size }) {
  const t = useT();
  return (
    <div className={size === "card" ? "space-y-1.5" : "grid gap-2 md:grid-cols-2"}>
      {w.entities.map((e) => (
        <motion.div key={e.id} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="rounded-md border border-[color:var(--agx-line)] agx-wash px-2 py-1.5">
          <div className="flex items-center gap-2">
            <span className="size-1.5 rounded-full" style={{ background: TONE_COLOR[e.tone] }} />
            <p className="min-w-0 flex-1 truncate text-[11.5px] font-medium">{t(e.name)}</p>
            <span className="agx-mono text-[11px] agx-dim">{e.id}</span>
          </div>
          <p className="mt-0.5 truncate text-[11px]" style={{ color: TONE_COLOR[e.tone] }}>{t(e.status)}</p>
          {size === "focus" ? e.lines.map((l, i) => <p key={i} className="text-[11.5px] agx-dim">{t(l)}</p>) : null}
        </motion.div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- measures

function Meters({ w, size }: { w: Extract<Widget, { kind: "meters" }>; size: Size }) {
  const t = useT();
  return (
    <div className={size === "card" ? "space-y-2" : "space-y-4"}>
      {w.meters.map((m, i) => {
        const pos = Math.min(100, (m.value / Math.max(1e-9, m.max)) * 100);
        const thr = Math.min(100, (m.threshold / Math.max(1e-9, m.max)) * 100);
        const color = m.met ? "var(--agx-gold)" : "var(--agx-cyan)";
        return (
          <div key={i}>
            <div className="mb-1 flex items-baseline justify-between gap-2 text-[11px]">
              <span className="truncate agx-dim">{t(m.label)}</span>
              <span className="agx-mono shrink-0 font-semibold" style={{ color }}>{t(m.display)}</span>
            </div>
            <div className="relative h-2 rounded-full bg-[color:var(--agx-chart-line)]">
              <motion.div className="absolute inset-y-0 start-0 rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${color})` }} initial={{ width: 0 }} animate={{ width: `${pos}%` }} transition={{ duration: 0.9, ease: "easeOut" }} />
              {m.threshold > 0 ? <span className="absolute -top-1 h-4 w-px bg-[color:var(--agx-text)] opacity-70" style={{ insetInlineStart: `${thr}%` }} title={t(m.thresholdLabel)} /> : null}
            </div>
            {size === "focus" ? <p className="mt-1 text-[11px] agx-faint">{t(m.thresholdLabel)}</p> : null}
          </div>
        );
      })}
    </div>
  );
}

function Bars({ w, size }: { w: Extract<Widget, { kind: "bars" }>; size: Size }) {
  const t = useT();
  const max = w.max ?? Math.max(1, ...w.bars.map((b) => Math.max(b.value, b.compare ?? 0)));
  const thr = w.threshold ? Math.min(100, (w.threshold.value / max) * 100) : null;
  return (
    <div className="relative">
      {w.legend ? (
        <div className="mb-1 flex gap-3 text-[11px] agx-dim">
          <span className="flex items-center gap-1"><i className="h-1.5 w-3 rounded-sm" style={{ background: "var(--sq)" }} />{t(w.legend[0])}</span>
          <span className="flex items-center gap-1"><i className="h-1.5 w-3 rounded-sm bg-[color:var(--agx-dim)]" />{t(w.legend[1])}</span>
        </div>
      ) : null}
      <div className={size === "card" ? "space-y-[5px]" : "space-y-2.5"}>
        {w.bars.slice(0, size === "card" ? 6 : 30).map((b, i) => {
          const color = b.tone ? TONE_COLOR[b.tone] : b.highlight ? "var(--agx-gold)" : "var(--agx-bar-muted)";
          return (
            <div key={`${b.label.en}-${i}`} className="grid grid-cols-[minmax(0,40%)_minmax(0,1fr)] items-center gap-2">
              <span className={`truncate text-[11px] ${b.highlight ? "font-semibold text-[color:var(--agx-gold-2)]" : "agx-dim"}`}>{t(b.label)}</span>
              <div className="relative">
                <motion.div className="h-[7px] rounded-sm" style={{ background: color }} initial={{ width: 0 }} animate={{ width: `${Math.min(100, (b.value / max) * 100)}%` }} transition={{ duration: 0.7, ease: "easeOut" }} />
                {b.compare != null ? <motion.div className="mt-[2px] h-[3px] rounded-sm bg-[color:var(--agx-dim)]" initial={{ width: 0 }} animate={{ width: `${Math.min(100, (b.compare / max) * 100)}%` }} transition={{ duration: 0.7, delay: 0.15 }} /> : null}
                <span className="agx-mono absolute -top-[1px] text-[9.5px] text-[color:var(--agx-text)]" style={{ insetInlineStart: `calc(${Math.min(88, (Math.max(b.value, b.compare ?? 0) / max) * 100)}% + 4px)` }}>{b.display ?? `${b.value}${w.unit}`}{b.compare != null ? ` → ${b.compare}${w.unit}` : ""}</span>
              </div>
            </div>
          );
        })}
      </div>
      {thr != null ? <span className="pointer-events-none absolute bottom-0 top-0 w-px bg-[color:var(--agx-rose)]/70" style={{ insetInlineStart: `calc(40% + 0.5rem + (60% - 0.5rem) * ${thr / 100})` }} title={w.threshold ? t(w.threshold.label) : undefined} /> : null}
    </div>
  );
}

function GaugeView({ w, size }: { w: Extract<Widget, { kind: "gauge" }>; size: Size }) {
  const t = useT();
  const r = 70;
  const angle = (v: number) => Math.PI * (1 - Math.min(1, Math.max(0, v / w.max)));
  const pt = (v: number, rad = r) => [100 + rad * Math.cos(angle(v)), 92 - rad * Math.sin(angle(v))] as const;
  const arc = (a: number, b: number, rad = r) => { const [x1, y1] = pt(a, rad); const [x2, y2] = pt(b, rad); return `M ${x1} ${y1} A ${rad} ${rad} 0 0 1 ${x2} ${y2}`; };
  let from = 0;
  const band = w.bands.find((b) => w.value <= b.to) ?? w.bands[w.bands.length - 1];
  const [nx, ny] = pt(w.value, r - 14);
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <svg viewBox="0 0 200 104" className={size === "card" ? "h-[86px] w-auto" : "h-56 w-auto"}>
        {w.bands.map((b) => { const d = arc(from, b.to); from = b.to; return <path key={b.label.en} d={d} stroke={TONE_COLOR[b.tone]} strokeOpacity={0.22} strokeWidth={12} fill="none" />; })}
        <motion.path d={arc(0, Math.max(0.0001, w.value))} stroke={TONE_COLOR[band?.tone ?? "info"]} strokeWidth={12} fill="none" strokeLinecap="round"  />
        {w.secondary ? (() => { const [sx, sy] = pt(w.secondary.value, r + 9); return <circle cx={sx} cy={sy} r={3} fill="var(--agx-text)" opacity={0.7} />; })() : null}
        <line x1={100} y1={92} x2={nx} y2={ny} stroke="var(--agx-text)" strokeWidth={2} strokeLinecap="round" />
        <circle cx={100} cy={92} r={4} fill="var(--agx-text)" />
        <text x={100} y={74} textAnchor="middle" className="agx-mono" fontSize={22} fontWeight={700} fill="var(--agx-text)">{Math.round(w.value)}</text>
      </svg>
      <p className="-mt-1 text-[11px] font-semibold uppercase tracking-wider" style={{ color: TONE_COLOR[band?.tone ?? "info"] }}>{band ? t(band.label) : ""}</p>
      <p className="text-[11px] agx-dim">{t(w.label)}{w.secondary ? ` · ${t(w.secondary.label)} ${Math.round(w.secondary.value)}` : ""}</p>
      {w.caption ? <p className="mt-0.5 text-center text-[11px] agx-faint">{t(w.caption)}</p> : null}
    </div>
  );
}

function Stake({ w, size }: { w: Extract<Widget, { kind: "stake" }>; size: Size }) {
  const t = useT();
  return (
    <div className={size === "card" ? "space-y-2" : "space-y-4"}>
      <div className="relative h-4 overflow-hidden rounded-md bg-[color:var(--agx-chart-line)]">
        <motion.div className="absolute inset-y-0 start-0 bg-[color:var(--agx-faint)] opacity-60" initial={{ width: 0 }} animate={{ width: `${w.before}%` }} />
        <motion.div className="absolute inset-y-0" style={{ insetInlineStart: `${w.before}%`, background: "linear-gradient(90deg, var(--agx-gold), var(--agx-gold-2))" }} initial={{ width: 0 }} animate={{ width: `${w.acquired}%` }} transition={{ duration: 1.1, ease: "easeOut" }} />
        <span className="absolute inset-y-0 w-px bg-white" style={{ insetInlineStart: "50%" }} />
        <span className="agx-mono absolute inset-y-0 flex items-center px-1.5 text-[9.5px] font-bold text-[color:var(--agx-on-accent)]" style={{ insetInlineStart: `${w.before}%` }}>{w.acquired > 8 ? `+${w.acquired}%` : ""}</span>
      </div>
      <div className="flex justify-between text-[11px] agx-faint"><span>0%</span><span>50% · control</span><span>{w.after}% after</span></div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="agx-chip" style={{ ["--c" as string]: TONE_COLOR[w.tone] }}>{t(w.verdict)}</span>
        {w.rights.map((r, i) => <span key={i} className="agx-chip" style={{ ["--c" as string]: "var(--agx-violet)" }}>{t(r)}</span>)}
      </div>
      {w.reading && size === "focus" ? <p className="text-[12.5px] leading-5 agx-dim">{t(w.reading)}</p> : w.reading ? <p className="line-clamp-2 text-[11px] agx-dim">{t(w.reading)}</p> : null}
    </div>
  );
}

function Timeline({ w, size }: { w: Extract<Widget, { kind: "timeline" }>; size: Size }) {
  const t = useT();
  return (
    <div className="flex h-full flex-col justify-center">
      <div className="relative mx-2 h-px bg-[color:var(--agx-line-2)]">
        {w.events.map((e, i) => {
          const pos = w.events.length === 1 ? 50 : (i / (w.events.length - 1)) * 100;
          const color = TONE_COLOR[e.tone ?? (e.state === "past" ? "ok" : e.state === "now" ? "info" : "warn")];
          return (
            <div key={i} className="absolute -top-[5px]" style={{ insetInlineStart: `calc(${pos}% - 5px)` }}>
              <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.15 }} className="block size-[10px] rounded-full border-2" style={{ borderColor: color, background: e.state === "past" ? color : "var(--agx-solid)", boxShadow: e.state === "now" ? `0 0 0 4px color-mix(in oklab, ${color} 25%, transparent)` : undefined }} />
            </div>
          );
        })}
      </div>
      <div className="mt-4 grid gap-1" style={{ gridTemplateColumns: `repeat(${Math.max(1, w.events.length)}, minmax(0, 1fr))` }}>
        {w.events.map((e, i) => (
          <div key={i} className={`min-w-0 ${i === 0 ? "text-start" : i === w.events.length - 1 ? "text-end" : "text-center"}`}>
            <p className="agx-mono text-[9.5px] agx-faint">{e.date}</p>
            <p className={`${size === "card" ? "line-clamp-2 text-[11px]" : "text-[12px]"} text-[color:var(--agx-text)]/85`}>{t(e.label)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- charts

function Series({ w, size }: { w: Extract<Widget, { kind: "series" }>; size: Size }) {
  const t = useT();
  const W = 280, H = size === "card" ? 92 : 220, P = 18;
  const all = w.series.flatMap((s) => s.values);
  const lo = Math.min(...all) * 0.9, hi = Math.max(...all) * 1.08;
  const x = (i: number) => P + (w.categories.length <= 1 ? (W - 2 * P) / 2 : (i / (Math.max(3, w.categories.length) - 1)) * (W - 2 * P));
  const y = (v: number) => H - P - ((v - lo) / Math.max(1, hi - lo)) * (H - 2 * P);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: size === "card" ? 92 : 220 }}>
        {[0.25, 0.5, 0.75].map((g) => <line key={g} x1={P} x2={W - P} y1={P + g * (H - 2 * P)} y2={P + g * (H - 2 * P)} stroke="var(--agx-chart-line)" />)}
        {w.series.map((s) => {
          const color = TONE_COLOR[s.tone ?? "info"];
          const d = s.values.map((v, i) => `${i ? "L" : "M"} ${x(i)} ${y(v)}`).join(" ");
          return (
            <g key={s.name.en}>
              <motion.path d={d} fill="none" stroke={color} strokeWidth={2} initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.8 }} style={{ filter: `drop-shadow(0 0 4px ${color})` }} />
              {s.values.map((v, i) => <g key={i}><circle cx={x(i)} cy={y(v)} r={3} fill={color} /><text x={x(i)} y={y(v) - 6} textAnchor="middle" fontSize={9} fill="var(--agx-text)" className="agx-mono">{v}</text></g>)}
            </g>
          );
        })}
        {w.categories.map((c, i) => <text key={c} x={x(i)} y={H - 4} textAnchor="middle" fontSize={9} fill="var(--agx-faint)" className="agx-mono">{c}</text>)}
      </svg>
      <div className="flex flex-wrap gap-3 text-[11px] agx-dim">
        {w.series.map((s) => <span key={s.name.en} className="flex items-center gap-1"><i className="h-[3px] w-3 rounded" style={{ background: TONE_COLOR[s.tone ?? "info"] }} />{t(s.name)}</span>)}
        {w.caption ? <span className="ms-auto truncate agx-faint">{t(w.caption)}</span> : null}
      </div>
    </div>
  );
}

const NODE_COLOR = { market: "var(--agx-cyan)", party: "var(--agx-gold)", competitor: "var(--agx-bar-muted)", holder: "var(--agx-violet)" };

function Graph({ w, size }: { w: Extract<Widget, { kind: "graph" }>; size: Size }) {
  const t = useT();
  const W = size === "card" ? 280 : 640, H = size === "card" ? 112 : 380;
  const center = w.nodes.find((n) => n.kind === "market");
  const others = w.nodes.filter((n) => n !== center);
  const pos = new Map<string, [number, number]>();
  if (center) pos.set(center.id, [W / 2, H / 2]);
  others.forEach((n, i) => {
    const a = -Math.PI / 2 + (i / Math.max(1, others.length)) * Math.PI * 2;
    pos.set(n.id, [W / 2 + Math.cos(a) * (W / 2 - (size === "card" ? 30 : 90)), H / 2 + Math.sin(a) * (H / 2 - (size === "card" ? 14 : 40))]);
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: H }}>
      {w.links.map((l, i) => {
        const a = pos.get(l.from), b = pos.get(l.to);
        if (!a || !b) return null;
        return <motion.line key={i} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke="var(--agx-line-2)" strokeWidth={Math.max(0.8, Math.min(3, (l.strength ?? 2) / 4))} initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} />;
      })}
      {w.nodes.map((n) => {
        const p = pos.get(n.id)!;
        const r = n.kind === "market" ? (size === "card" ? 10 : 18) : Math.max(size === "card" ? 4 : 7, Math.min(size === "card" ? 9 : 16, (n.weight ?? 3) * (size === "card" ? 0.6 : 1.1)));
        return (
          <motion.g key={n.id} initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} style={{ transformOrigin: `${p[0]}px ${p[1]}px` }}>
            <circle cx={p[0]} cy={p[1]} r={r} fill={NODE_COLOR[n.kind]} fillOpacity={n.kind === "competitor" ? 0.55 : 0.9}  />
            <text x={p[0]} y={p[1] + r + (size === "card" ? 8 : 13)} textAnchor="middle" fontSize={size === "card" ? 7.5 : 11} fill="var(--agx-dim)">{shortName(t(n.label), size === "card" ? 16 : 32)}{n.weight != null && n.kind !== "market" ? ` ${n.weight}%` : ""}</text>
          </motion.g>
        );
      })}
    </svg>
  );
}
const shortName = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

function TextDoc({ w, size }: { w: Extract<Widget, { kind: "text" }>; size: Size }) {
  const t = useT();
  const sections = size === "card" ? w.sections.slice(-2) : w.sections;
  return (
    <div className={size === "card" ? "space-y-1.5" : "space-y-4"}>
      <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--sq)" }}>{t(w.title)}</p>
      {sections.map((s, i) => {
        const last = s === w.sections[w.cursor];
        return (
          <div key={s.heading.en}>
            <p className={`${size === "card" ? "text-[11px]" : "text-[14px]"} font-semibold text-[color:var(--agx-text)]`}>{t(s.heading)}</p>
            <p className={`${size === "card" ? "line-clamp-2 text-[11px] leading-4" : "text-[13px] leading-6"} agx-dim`}>
              {last ? <Typewriter text={t(s.body)} ms={Math.min(2600, t(s.body).length * 14)} /> : t(s.body)}
            </p>
            {size === "focus" && i < sections.length - 1 ? <div className="mt-3 h-px bg-[color:var(--agx-line)]" /> : null}
          </div>
        );
      })}
    </div>
  );
}
